import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { formatCents, dollarsToCents } from '../lib/money';
import StatusBadge from '../components/StatusBadge.jsx';
import CardSetup from '../components/CardSetup.jsx';
import SubscriptionBuilder from '../components/SubscriptionBuilder.jsx';

function formatAddress(addr) {
  if (!addr || !addr.line1) return '—';
  return [addr.line1, addr.line2, [addr.city, addr.state].filter(Boolean).join(', '), addr.postalCode].filter(Boolean).join(', ');
}

function ReasonSelect({ codes, value, onChange }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} required>
      <option value="">Select a reason…</option>
      {codes.map((c) => (
        <option key={c} value={c}>
          {c.replace(/_/g, ' ')}
        </option>
      ))}
    </select>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [openAction, setOpenAction] = useState(null); // 'refund' | 'cancel' | 'pause' | 'resume'
  const [refundReasonCodes, setRefundReasonCodes] = useState([]);
  const [cancelReasonCodes, setCancelReasonCodes] = useState([]);
  const [pauseReasonCodes, setPauseReasonCodes] = useState([]);
  const [charges, setCharges] = useState([]);

  const [refundForm, setRefundForm] = useState({ stripeChargeId: '', amountDollars: '', reasonCode: '', reasonText: '' });
  const [cancelForm, setCancelForm] = useState({ reasonCode: '', reasonText: '' });
  const [pauseForm, setPauseForm] = useState({ reasonCode: '', reasonText: '', resumeAt: '' });
  const [submitting, setSubmitting] = useState(false);

  const [linkingHcp, setLinkingHcp] = useState(false);
  const [hcpLinkInput, setHcpLinkInput] = useState('');
  const [hcpLinkError, setHcpLinkError] = useState(null);
  const [creatingHcpJob, setCreatingHcpJob] = useState(false);
  const [hcpJobError, setHcpJobError] = useState(null);
  // Reminder pop-up for the CSR themselves, right when they cancel or pause
  // an account -- separate from (and earlier than) the admin-facing HCP job
  // pop-up on the Approvals page: that one fires only once a cancellation
  // is actually approved and links the JOB; this one fires the moment the
  // CSR takes the action (pause is immediate; cancel is still only a
  // request, but the CSR is the one on the phone with the customer right
  // now) and links the CUSTOMER, since a pause has no specific job to point
  // at. Only shows when this order actually has a linked HCP customer.
  const [hcpCustomerReminder, setHcpCustomerReminder] = useState(false);

  // #9/#10 from docs/OPEN_ITEMS.md (Dan, 2026-09-20): payment method summary
  // plus the two card-update flows -- #9's self-serve link (customer puts
  // in a new card themselves) and #10's CSR-driven live-call flow.
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [cardUpdateLink, setCardUpdateLink] = useState(null);
  const [requestingCardUpdate, setRequestingCardUpdate] = useState(false);
  const [cardUpdateError, setCardUpdateError] = useState(null);
  const [showChangeCardForm, setShowChangeCardForm] = useState(false);
  const [changeCardClientSecret, setChangeCardClientSecret] = useState(null);
  const [startingChangeCard, setStartingChangeCard] = useState(false);
  const [changeCardError, setChangeCardError] = useState(null);
  const [confirmingChangeCard, setConfirmingChangeCard] = useState(false);

  // Item 4 (Dan, 2026-09-21): change the plan tier / add-ons on an
  // existing subscription -- previously the only way to do this was
  // outside the tool entirely. `products` mirrors CreateOrder.jsx's fetch
  // so SubscriptionBuilder has the same allowlist/pricing to work from.
  const [products, setProducts] = useState([]);
  const [showChangePlanForm, setShowChangePlanForm] = useState(false);
  const [loadingCurrentPlan, setLoadingCurrentPlan] = useState(false);
  const [changePlanValue, setChangePlanValue] = useState(null);
  const [changePlanError, setChangePlanError] = useState(null);
  const [submittingPlanChange, setSubmittingPlanChange] = useState(false);

  async function load() {
    try {
      const { order, events } = await api.get(`/orders/${id}`);
      setOrder(order);
      setEvents(events);
      return order;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }

  async function loadPaymentMethod() {
    try {
      const { paymentMethod } = await api.get(`/orders/${id}/payment-method`);
      setPaymentMethod(paymentMethod);
    } catch (err) {
      // Non-fatal -- the rest of the page is still useful without this.
    }
  }

  useEffect(() => {
    load();
    loadPaymentMethod();
    api.get('/refunds/reason-codes').then((r) => setRefundReasonCodes(r.reasonCodes)).catch(() => {});
    api.get('/cancellations/reason-codes').then((r) => setCancelReasonCodes(r.reasonCodes)).catch(() => {});
    api.get('/subscriptions/reason-codes').then((r) => setPauseReasonCodes(r.reasonCodes)).catch(() => {});
    api.get('/products').then((r) => setProducts(r.products)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // WS8 (Dan, 2026-09-20): the HCP job row below is created fire-and-forget
  // in the background (orderService.createOrder), so the "Create HCP job"
  // button can go stale the moment the job actually shows up -- previously
  // the only way to see it flip to a real "Open in HCP" link was to
  // navigate away and back. Poll for a bit so it updates on its own.
  // Stops the moment the job appears, or after HCP_JOB_POLL_TIMEOUT_MS so a
  // job that genuinely never gets created (a real HCP failure -- see the
  // button/hcpJobError below) doesn't leave this hammering the API forever.
  useEffect(() => {
    if (!order) return;
    if (order.origin_channel === 'external') return; // never auto-created for these
    if (order.hcp_job_id) return; // already have it, nothing to poll for

    const HCP_JOB_POLL_INTERVAL_MS = 5000;
    const HCP_JOB_POLL_TIMEOUT_MS = 2 * 60 * 1000;
    const startedAt = Date.now();

    const intervalId = setInterval(() => {
      if (Date.now() - startedAt > HCP_JOB_POLL_TIMEOUT_MS) {
        clearInterval(intervalId);
        return;
      }
      load();
    }, HCP_JOB_POLL_INTERVAL_MS);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.hcp_job_id, order?.origin_channel, id]);

  async function openRefund() {
    setOpenAction('refund');
    try {
      const { charges } = await api.get(`/orders/${id}/charges`);
      setCharges(charges);
    } catch (err) {
      setError(err.message);
    }
  }

  async function submitRefund(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const requestId = crypto.randomUUID();
      const { event } = await api.post('/refunds', {
        orderId: id,
        stripeChargeId: refundForm.stripeChargeId,
        amountCents: dollarsToCents(refundForm.amountDollars),
        reasonCode: refundForm.reasonCode,
        reasonText: refundForm.reasonText,
        requestId,
      });
      setNotice(
        event.status === 'pending_approval'
          ? 'Refund submitted for admin approval (threshold exceeded).'
          : 'Refund processed.'
      );
      setOpenAction(null);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function submitCancel(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const requestId = crypto.randomUUID();
      await api.post('/cancellations', { orderId: id, ...cancelForm, requestId });
      setNotice('Cancellation submitted for admin approval.');
      setOpenAction(null);
      // Re-check against a FRESH copy of the order, not the possibly-stale
      // `order` in this closure -- HCP linking happens in the background,
      // so a page that's been open a while (or opened right after order
      // creation) can easily be looking at a customer_id that's since
      // shown up on the server but never made it back to this component.
      const fresh = await load();
      if (fresh?.customer_id) setHcpCustomerReminder(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function submitPause(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const requestId = crypto.randomUUID();
      await api.post('/subscriptions/pause', {
        orderId: id,
        reasonCode: pauseForm.reasonCode,
        reasonText: pauseForm.reasonText,
        resumeAt: pauseForm.resumeAt || null,
        requestId,
      });
      setNotice('Plan paused.');
      setOpenAction(null);
      // Same staleness fix as submitCancel above -- check a fresh copy of
      // the order, not the one this closure was holding onto.
      const fresh = await load();
      if (fresh?.customer_id) setHcpCustomerReminder(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  async function submitHcpLink(e) {
    e.preventDefault();
    setLinkingHcp(true);
    setHcpLinkError(null);
    try {
      await api.patch(`/orders/${id}/hcp-link`, { hcpCustomerIdOrUrl: hcpLinkInput });
      setHcpLinkInput('');
      setNotice('Linked to HCP customer.');
      load();
    } catch (err) {
      setHcpLinkError(err.message);
    } finally {
      setLinkingHcp(false);
    }
  }

  async function submitCreateHcpJob() {
    setCreatingHcpJob(true);
    setHcpJobError(null);
    try {
      await api.post(`/orders/${id}/create-hcp-job`, {});
      setNotice('HCP job created — it will show up in HCP\'s "needs scheduling" queue.');
      load();
    } catch (err) {
      setHcpJobError(err.message);
    } finally {
      setCreatingHcpJob(false);
    }
  }

  async function requestCardUpdateLink() {
    setRequestingCardUpdate(true);
    setCardUpdateError(null);
    try {
      const requestId = crypto.randomUUID();
      const { url } = await api.post(`/orders/${id}/request-card-update`, { requestId });
      setCardUpdateLink(url);
      load(); // picks up the new 'card_update_requested' History row
    } catch (err) {
      setCardUpdateError(err.message);
    } finally {
      setRequestingCardUpdate(false);
    }
  }

  async function startChangeCard() {
    setShowChangeCardForm(true);
    setChangeCardError(null);
    setStartingChangeCard(true);
    try {
      const requestId = crypto.randomUUID();
      const { clientSecret } = await api.post(`/orders/${id}/change-card/setup-intent`, { requestId });
      setChangeCardClientSecret(clientSecret);
    } catch (err) {
      setChangeCardError(err.message);
    } finally {
      setStartingChangeCard(false);
    }
  }

  async function handleChangeCardConfirmed(paymentMethodId) {
    setConfirmingChangeCard(true);
    setChangeCardError(null);
    try {
      await api.post(`/orders/${id}/change-card/confirm`, { paymentMethodId });
      setNotice('Card on file updated.');
      setShowChangeCardForm(false);
      setChangeCardClientSecret(null);
      loadPaymentMethod();
      load(); // picks up the new 'card_updated' History row
    } catch (err) {
      setChangeCardError(err.message);
    } finally {
      setConfirmingChangeCard(false);
    }
  }

  async function startChangePlan() {
    setShowChangePlanForm(true);
    setChangePlanError(null);
    setLoadingCurrentPlan(true);
    try {
      const { priceId, addonPriceIds } = await api.get(`/orders/${id}/plan`);
      setChangePlanValue({
        priceId: priceId || '',
        planTier: order.plan_tier,
        addonPriceIds: addonPriceIds || [],
        // Fees are out of scope for a plan change (see SubscriptionBuilder's
        // hideFees prop) -- these are only here because SubscriptionBuilder
        // expects the full shape it also uses for brand-new orders.
        initialFeePriceId: null,
        initialFeeCents: 0,
        initialFeeDisplayName: null,
        extraFeePriceIds: [],
      });
    } catch (err) {
      setChangePlanError(err.message);
    } finally {
      setLoadingCurrentPlan(false);
    }
  }

  async function submitChangePlan(e) {
    e.preventDefault();
    setSubmittingPlanChange(true);
    setChangePlanError(null);
    try {
      const requestId = crypto.randomUUID();
      await api.post(`/orders/${id}/change-plan`, {
        requestId,
        priceId: changePlanValue.priceId,
        addonPriceIds: changePlanValue.addonPriceIds,
        planTier: changePlanValue.planTier,
      });
      setNotice('Plan updated.');
      setShowChangePlanForm(false);
      setChangePlanValue(null);
      load(); // picks up the new plan_tier/addons/monthly_price_cents and 'plan_changed' History row
    } catch (err) {
      setChangePlanError(err.message);
    } finally {
      setSubmittingPlanChange(false);
    }
  }

  async function submitResume() {
    setSubmitting(true);
    setError(null);
    try {
      const requestId = crypto.randomUUID();
      await api.post('/subscriptions/resume', { orderId: id, requestId });
      setNotice('Plan resumed.');
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (!order) return error ? <div className="error-banner">{error}</div> : <p>Loading…</p>;

  const latestCommissionFlag = events.find((e) => e.event_type === 'canceled' && e.commission_flag);

  return (
    <div>
      <h2>Plan {order.id.slice(0, 8)}</h2>
      {error && <div className="error-banner">{error}</div>}
      {notice && <div className="card">{notice}</div>}

      {latestCommissionFlag && (
        <div className="commission-flag">
          <strong>Commission flag:</strong> {latestCommissionFlag.commission_flag_reason}
        </div>
      )}

      {order.status === 'past_due' && (
        <div className="past-due-flag">
          <strong>Payment past due.</strong>{' '}
          {order.last_payment_failed_at
            ? `Last failed attempt: ${new Date(order.last_payment_failed_at).toLocaleString()}.`
            : ''}
          {typeof order.payment_attempt_count === 'number' ? ` Attempt #${order.payment_attempt_count}.` : ''}
          {order.next_payment_attempt_at
            ? ` Stripe will retry on ${new Date(order.next_payment_attempt_at).toLocaleDateString()}.`
            : ' No further automatic retry is scheduled.'}{' '}
          Use Send card update link or Change card on file below to fix it.
        </div>
      )}

      <div className="card">
        <table>
          <tbody>
            <tr><th>Customer</th><td>{order.customer_name || '—'}</td></tr>
            <tr><th>Email</th><td>{order.customer_email || '—'}</td></tr>
            <tr><th>Phone</th><td>{order.customer_phone || '—'}</td></tr>
            <tr><th>Billing address</th><td>{formatAddress(order.billing_address)}</td></tr>
            <tr><th>Service address</th><td>{formatAddress(order.service_address)}</td></tr>
            <tr><th>Coupon</th><td>{order.coupon_code || '—'}</td></tr>
            <tr>
              <th>HouseCallPro customer</th>
              <td>
                {order.customer_id ? (
                  <>
                    <a href={`https://pro.housecallpro.com/app/customers/${order.customer_id}`} target="_blank" rel="noreferrer">
                      Open in HCP
                    </a>
                    {order.hcp_linked_by && (
                      <span style={{ fontSize: 12, color: 'var(--color-text-muted)', marginLeft: 8 }}>
                        (linked by {order.hcp_linked_by})
                      </span>
                    )}
                  </>
                ) : (
                  <form onSubmit={submitHcpLink} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ color: 'var(--color-text-muted)' }}>Not linked yet —</span>
                    <input
                      type="text"
                      placeholder="Paste HCP customer URL or id"
                      value={hcpLinkInput}
                      onChange={(e) => setHcpLinkInput(e.target.value)}
                      style={{ minWidth: 280 }}
                      required
                    />
                    <button type="submit" className="btn" disabled={linkingHcp}>
                      {linkingHcp ? 'Linking…' : 'Link'}
                    </button>
                  </form>
                )}
                {hcpLinkError && (
                  <div style={{ color: 'var(--color-danger)', fontSize: 12, marginTop: 4 }}>{hcpLinkError}</div>
                )}
              </td>
            </tr>
            {order.origin_channel !== 'external' && (
              <tr>
                <th>HouseCallPro job</th>
                <td>
                  {order.hcp_job_id ? (
                    <a href={`https://pro.housecallpro.com/app/jobs/${order.hcp_job_id}`} target="_blank" rel="noreferrer">
                      Open in HCP
                    </a>
                  ) : (
                    <>
                      <button type="button" className="btn" onClick={submitCreateHcpJob} disabled={creatingHcpJob}>
                        {creatingHcpJob ? 'Creating…' : 'Create HCP job'}
                      </button>
                      <span style={{ fontSize: 12, color: 'var(--color-text-muted)', marginLeft: 8 }}>
                        This normally happens automatically right after the order is created. Use this button only as a
                        backup — if it hasn't shown up after a minute, or the HCP job was deleted by mistake. Creates
                        the customer (if not already linked above) and a seed job in HCP's needs-scheduling queue.
                      </span>
                    </>
                  )}
                  {hcpJobError && (
                    <div style={{ color: 'var(--color-danger)', fontSize: 12, marginTop: 4 }}>{hcpJobError}</div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Actions</h3>
        <p className="hint-text" style={{ marginTop: 0 }}>
          Card on file: {paymentMethod
            ? `${paymentMethod.brand} ending ${paymentMethod.last4}, exp ${paymentMethod.expMonth}/${paymentMethod.expYear}`
            : 'no card on file.'}
        </p>

        {/* Grouped + ordered by severity, left to right (Dan, 2026-09-21) --
            card-on-file actions are their own row since they're a
            different kind of thing entirely (nobody's service is affected),
            then the subscription-affecting actions run neutral -> warning
            -> destructive so Cancel is never just one more button in a row
            that all look the same. */}
        <div className="stage-label">Card on file</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <button className="btn" onClick={requestCardUpdateLink} disabled={requestingCardUpdate || order.status === 'canceled'}>
            {requestingCardUpdate ? 'Generating link…' : 'Send card update link'}
          </button>
          <button className="btn" onClick={startChangeCard} disabled={showChangeCardForm || order.status === 'canceled'}>
            Change card on file
          </button>
        </div>

        <div className="stage-label">Subscription</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn" onClick={openRefund} disabled={order.status === 'canceled'}>Refund</button>
          <button className="btn" onClick={startChangePlan} disabled={showChangePlanForm || order.status === 'canceled'}>
            Change plan
          </button>
          {order.status !== 'paused' ? (
            <button className="btn btn-warning" onClick={() => setOpenAction('pause')} disabled={order.status === 'canceled'}>Pause</button>
          ) : (
            <button className="btn btn-warning" onClick={submitResume} disabled={submitting}>Resume</button>
          )}
          <button className="btn btn-danger" onClick={() => setOpenAction('cancel')} disabled={order.status === 'canceled'}>Cancel</button>
        </div>
        <p className="hint-text" style={{ marginTop: 8 }}>
          "Send card update link" generates a Stripe-hosted page for the customer to put in a new card themselves --
          copy it below and send it however you currently reach them (text, email, etc). "Change card on file" is for
          collecting a new card yourself, live on a call.
        </p>

        {cardUpdateError && <div className="error-banner">{cardUpdateError}</div>}
        {cardUpdateLink && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
            <input type="text" readOnly value={cardUpdateLink} style={{ minWidth: 320, flex: 1 }} onFocus={(e) => e.target.select()} />
            <button
              type="button"
              className="btn"
              onClick={() => {
                navigator.clipboard?.writeText(cardUpdateLink);
                setNotice('Link copied to clipboard.');
              }}
            >
              Copy
            </button>
          </div>
        )}

        {showChangeCardForm && (
          <div style={{ marginTop: 16 }}>
            {changeCardError && <div className="error-banner">{changeCardError}</div>}
            {startingChangeCard && <p>Setting up…</p>}
            {changeCardClientSecret && (
              <CardSetup
                clientSecret={changeCardClientSecret}
                billingDetails={{
                  name: order.customer_name || undefined,
                  email: order.customer_email || undefined,
                  phone: order.customer_phone || undefined,
                }}
                onConfirmed={handleChangeCardConfirmed}
              />
            )}
            {confirmingChangeCard && <p>Applying new card…</p>}
            <button
              type="button"
              className="btn"
              style={{ marginTop: 8 }}
              onClick={() => {
                setShowChangeCardForm(false);
                setChangeCardClientSecret(null);
                setChangeCardError(null);
              }}
            >
              Cancel
            </button>
          </div>
        )}

        {showChangePlanForm && (
          <form onSubmit={submitChangePlan} style={{ marginTop: 16 }}>
            {changePlanError && <div className="error-banner">{changePlanError}</div>}
            {loadingCurrentPlan && <p>Loading current plan…</p>}
            {changePlanValue && (
              <SubscriptionBuilder
                products={products}
                value={changePlanValue}
                onChange={setChangePlanValue}
                hideFees
                canRemove={false}
              />
            )}
            <button
              type="submit"
              className="btn btn-primary"
              style={{ marginTop: 8 }}
              disabled={submittingPlanChange || !changePlanValue?.priceId}
            >
              {submittingPlanChange ? 'Saving…' : 'Save plan change'}
            </button>{' '}
            <button
              type="button"
              className="btn"
              style={{ marginTop: 8 }}
              onClick={() => {
                setShowChangePlanForm(false);
                setChangePlanValue(null);
                setChangePlanError(null);
              }}
            >
              Cancel
            </button>
          </form>
        )}

        {openAction === 'refund' && (
          <form onSubmit={submitRefund} style={{ marginTop: 16 }}>
            <label>Charge to refund</label>
            <select value={refundForm.stripeChargeId} onChange={(e) => setRefundForm({ ...refundForm, stripeChargeId: e.target.value })} required>
              <option value="">Select a charge…</option>
              {charges.map((c) => (
                <option key={c.id} value={c.id}>
                  {formatCents(c.amountCents)} — {new Date(c.created * 1000).toLocaleDateString()} {c.refunded ? '(already refunded)' : ''}
                </option>
              ))}
            </select>
            <label>Refund amount ($)</label>
            <input type="number" step="0.01" min="0" value={refundForm.amountDollars} onChange={(e) => setRefundForm({ ...refundForm, amountDollars: e.target.value })} required />
            <label>Reason</label>
            <ReasonSelect codes={refundReasonCodes} value={refundForm.reasonCode} onChange={(v) => setRefundForm({ ...refundForm, reasonCode: v })} />
            <label>Notes</label>
            <textarea value={refundForm.reasonText} onChange={(e) => setRefundForm({ ...refundForm, reasonText: e.target.value })} />
            <button type="submit" className="btn btn-primary" disabled={submitting}>Submit refund</button>{' '}
            <button type="button" className="btn" onClick={() => setOpenAction(null)}>Cancel</button>
          </form>
        )}

        {openAction === 'cancel' && (
          <form onSubmit={submitCancel} style={{ marginTop: 16 }}>
            <label>Reason</label>
            <ReasonSelect codes={cancelReasonCodes} value={cancelForm.reasonCode} onChange={(v) => setCancelForm({ ...cancelForm, reasonCode: v })} />
            <label>Notes</label>
            <textarea value={cancelForm.reasonText} onChange={(e) => setCancelForm({ ...cancelForm, reasonText: e.target.value })} />
            <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
              Cancellations always require admin approval before they're sent to Stripe.
            </p>
            <button type="submit" className="btn btn-primary" disabled={submitting}>Submit for approval</button>{' '}
            <button type="button" className="btn" onClick={() => setOpenAction(null)}>Cancel</button>
          </form>
        )}

        {openAction === 'pause' && (
          <form onSubmit={submitPause} style={{ marginTop: 16 }}>
            <label>Reason</label>
            <ReasonSelect codes={pauseReasonCodes} value={pauseForm.reasonCode} onChange={(v) => setPauseForm({ ...pauseForm, reasonCode: v })} />
            <label>Resume date (leave blank for indefinite)</label>
            <input type="date" value={pauseForm.resumeAt} onChange={(e) => setPauseForm({ ...pauseForm, resumeAt: e.target.value })} />
            <label>Notes</label>
            <textarea value={pauseForm.reasonText} onChange={(e) => setPauseForm({ ...pauseForm, reasonText: e.target.value })} />
            <button type="submit" className="btn btn-primary" disabled={submitting}>Pause plan</button>{' '}
            <button type="button" className="btn" onClick={() => setOpenAction(null)}>Cancel</button>
          </form>
        )}
      </div>

      <div className="card">
        <table>
          <tbody>
            <tr><th>Status</th><td><StatusBadge status={order.status} /></td></tr>
            <tr><th>Plan</th><td>{order.plan_tier}</td></tr>
            <tr><th>Monthly price</th><td>{formatCents(order.monthly_price_cents)}</td></tr>
            <tr>
              <th>Initial fee</th>
              <td>
                {formatCents(order.initial_fee_cents)}
                {Array.isArray(order.initial_fees) && order.initial_fees.length > 1 && (
                  <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 2 }}>
                    {order.initial_fees.map((f) => (
                      <div key={f.priceId}>{f.displayName} — {formatCents(f.amountCents)}</div>
                    ))}
                  </div>
                )}
              </td>
            </tr>
            <tr><th>Origin channel</th><td>{order.origin_channel}</td></tr>
            <tr><th>Attributed rep</th><td>{order.attributed_rep_id || '—'}</td></tr>
            <tr><th>Created by</th><td>{order.created_by}</td></tr>
            <tr><th>Stripe customer</th><td>{order.stripe_customer_id}</td></tr>
            <tr><th>Stripe subscription</th><td>{order.stripe_subscription_id}</td></tr>
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>History</h3>
        <table>
          <thead>
            <tr>
              <th>When</th><th>Event</th><th>Amount</th><th>Reason</th><th>Status</th><th>Actor</th><th>Approver</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.id}>
                <td>{new Date(e.created_at).toLocaleString()}</td>
                <td>{e.event_type}</td>
                <td>{formatCents(e.amount_cents)}</td>
                <td>{e.reason_code || '—'}</td>
                <td><StatusBadge status={e.status} /></td>
                <td>{e.actor_user_id}</td>
                <td>{e.approver_user_id || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hcpCustomerReminder && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3>Update HouseCallPro</h3>
            <p>
              Don't forget to update this customer's account in HouseCallPro too -- our tool doesn't do this for you.
            </p>
            <p>
              <a href={`https://pro.housecallpro.com/app/customers/${order.customer_id}`} target="_blank" rel="noreferrer">
                Open this customer in HouseCallPro
              </a>
            </p>
            <button type="button" className="btn btn-primary" onClick={() => setHcpCustomerReminder(false)}>
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
